import { useState } from 'react'
import { profileService } from '../../services/profileService'
import WorkExperienceForm from './WorkExperienceForm'
import type { WorkExperience, WorkExperienceInput } from '../../types'

interface WorkExperienceSectionProps {
  experiences: WorkExperience[]
  onUpdate: (experiences: WorkExperience[]) => void
}

export default function WorkExperienceSection({
  experiences,
  onUpdate,
}: WorkExperienceSectionProps) {
  const [showAddForm, setShowAddForm] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const handleCreate = async (data: WorkExperienceInput) => {
    const newExp = await profileService.createWorkExperience(data)
    onUpdate([...experiences, newExp])
    setShowAddForm(false)
  }

  const handleUpdate = async (id: number, data: WorkExperienceInput) => {
    const updated = await profileService.updateWorkExperience(id, data)
    onUpdate(experiences.map((e) => (e.id === id ? updated : e)))
    setEditingId(null)
  }

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this experience?')) return
    setDeletingId(id)
    try {
      await profileService.deleteWorkExperience(id)
      onUpdate(experiences.filter((e) => e.id !== id))
    } finally {
      setDeletingId(null)
    }
  }

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return ''
    const date = new Date(dateStr)
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
  }

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <svg
            className="w-5 h-5 text-indigo-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
          Work Experience
        </h2>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Add
          </button>
        )}
      </div>

      {showAddForm && (
        <div className="mb-6">
          <WorkExperienceForm onSubmit={handleCreate} onCancel={() => setShowAddForm(false)} />
        </div>
      )}

      {experiences.length === 0 && !showAddForm ? (
        <p className="text-gray-500 text-center py-8">No work experience added yet.</p>
      ) : (
        <div className="space-y-4">
          {experiences.map((exp) =>
            editingId === exp.id ? (
              <WorkExperienceForm
                key={exp.id}
                experience={exp}
                onSubmit={(data) => handleUpdate(exp.id, data)}
                onCancel={() => setEditingId(null)}
              />
            ) : (
              <div
                key={exp.id}
                className="group p-4 border border-gray-100 rounded-xl hover:border-gray-200 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{exp.job_title}</h3>
                    <p className="text-indigo-600">{exp.company_name}</p>
                    <div className="text-sm text-gray-500 mt-1">
                      {formatDate(exp.start_date)} -{' '}
                      {exp.is_current ? 'Present' : formatDate(exp.end_date)}
                      {exp.location && <span className="ml-2">• {exp.location}</span>}
                    </div>
                    {exp.description && (
                      <p className="text-gray-600 mt-2 text-sm whitespace-pre-line">
                        {exp.description}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      onClick={() => setEditingId(exp.id)}
                      className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                        />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(exp.id)}
                      disabled={deletingId === exp.id}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  )
}
